import { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminApi } from '../hooks/useAdminApi';
import { 
  Users, 
  MapPin, 
  Wrench, 
  AlertTriangle,
  TrendingUp,
  Clock,
  Truck,
  Activity,
  Container
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const { user, organization, hasModule } = useAdminAuth();
  const { get } = useAdminApi();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const result = await get('/saas/dashboard/overview');
      if (result.success) {
        setStats(result.data);
      }
      setLoading(false);
    };
    fetchStats();
  }, [get]);

  const StatCard = ({ icon: Icon, label, value, color, link }) => {
    const Content = (
      <div className={`bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-6 ${link ? 'hover:border-slate-700 transition-colors cursor-pointer' : ''}`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center ${color}`}>
            <Icon size={24} />
          </div>
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white">{value}</p>
            <p className="text-slate-400 text-sm">{label}</p>
          </div>
        </div>
      </div>
    );

    return link ? <Link to={link}>{Content}</Link> : Content;
  };

  const ModuleCard = ({ id, name, description, icon: Icon, active, link }) => (
    <Link 
      to={active ? link : '#'}
      className={`bg-slate-900 border rounded-xl p-4 md:p-6 flex items-center gap-4 transition-colors ${
        active 
          ? 'border-slate-800 hover:border-amber-500/50 cursor-pointer' 
          : 'border-slate-800/50 opacity-50 cursor-not-allowed'
      }`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${active ? 'bg-amber-500/20 text-amber-500' : 'bg-slate-800 text-slate-500'}`}>
        <Icon size={24} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="text-white font-semibold">{name}</p>
          {!active && (
            <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded">Non activé</span>
          )}
        </div>
        <p className="text-slate-400 text-sm">{description}</p>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-slate-900 rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            Bonjour, {user?.first_name}
          </h1>
          <p className="text-slate-400">
            {organization?.name || 'Administration'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Activity className="text-green-500" size={16} />
          <span className="text-green-400">Système opérationnel</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Users} 
          label="Chauffeurs partagés" 
          value={stats?.shared_drivers_count || 0}
          color="bg-blue-500/20 text-blue-500"
          link="/admin/drivers"
        />
        <StatCard 
          icon={MapPin} 
          label="En ligne" 
          value={stats?.online_drivers_count || 0}
          color="bg-green-500/20 text-green-500"
          link="/admin/map"
        />
        <StatCard 
          icon={AlertTriangle} 
          label="Alertes entretien" 
          value={stats?.maintenance_alerts_count || 0}
          color={stats?.high_priority_alerts > 0 ? "bg-red-500/20 text-red-500" : "bg-amber-500/20 text-amber-500"}
          link="/admin/maintenance"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Modules actifs" 
          value={stats?.active_modules?.length || 0}
          color="bg-purple-500/20 text-purple-500"
        />
      </div>

      {/* High Priority Alerts */}
      {stats?.high_priority_alerts > 0 && (
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 flex items-center gap-4">
          <AlertTriangle className="text-red-500 flex-shrink-0" size={24} />
          <div className="flex-1">
            <p className="text-red-400 font-semibold">
              {stats.high_priority_alerts} alerte(s) haute priorité
            </p>
            <p className="text-red-400/70 text-sm">
              Des entretiens sont en retard sur certains véhicules
            </p>
          </div>
          <Link 
            to="/admin/maintenance"
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Voir
          </Link>
        </div>
      )}

      {/* Modules Section */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Vos modules</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ModuleCard 
            id="module_entretiens"
            name="Suivi des Entretiens"
            description="Localisation, trajets, heures de service"
            icon={Wrench}
            active={hasModule('module_entretiens')}
            link="/admin/maintenance"
          />
          <ModuleCard 
            id="module_repartition"
            name="Répartition"
            description="Tableau de dispatch et assignation"
            icon={Truck}
            active={hasModule('module_repartition')}
            link="/admin/commandes"
          />
          <ModuleCard 
            id="module_facturation"
            name="Facturation"
            description="Temps, kilométrage, arrêts"
            icon={Clock}
            active={hasModule('module_facturation')}
            link="/admin/billing"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link 
            to="/admin/map"
            className="bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-xl p-4 text-center transition-colors"
          >
            <MapPin className="text-amber-500 mx-auto mb-2" size={28} />
            <p className="text-white font-medium">Carte en direct</p>
          </Link>
          <Link 
            to="/admin/drivers"
            className="bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-xl p-4 text-center transition-colors"
          >
            <Users className="text-amber-500 mx-auto mb-2" size={28} />
            <p className="text-white font-medium">Mes chauffeurs</p>
          </Link>
          <Link 
            to="/admin/maintenance"
            className="bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-xl p-4 text-center transition-colors"
          >
            <Wrench className="text-amber-500 mx-auto mb-2" size={28} />
            <p className="text-white font-medium">Entretiens</p>
          </Link>
          <Link 
            to="/admin/fleet"
            className="bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-xl p-4 text-center transition-colors"
          >
            <Container className="text-amber-500 mx-auto mb-2" size={28} />
            <p className="text-white font-medium">Flotte</p>
          </Link>
          <Link 
            to="/admin/settings"
            className="bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-xl p-4 text-center transition-colors"
          >
            <TrendingUp className="text-amber-500 mx-auto mb-2" size={28} />
            <p className="text-white font-medium">Paramètres</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
